/* ==========================================================================
   VERZENDBALK MET BELONINGSDREMPELS

   Houdt de balk bij terwijl de klant het mandje wijzigt. De eerste weergave
   komt uit Liquid, zie snippets/el-mandje-balk.liquid; dit bestand neemt het
   daarna over.

   ---------------------------------------------------------------------------
   Hoe hij weet dat het mandje wijzigde
   ---------------------------------------------------------------------------
   shopify:cart:lines-update op document. Let op: Horizon stuurt dat event
   uit VOORDAT het verzoek naar de server vertrekt (zie product-form.js en
   component-cart-items.js). Het resultaat zit in event.promise. Daarom
   wachten we eerst op die promise en halen we pas daarna /cart.js op;
   anders krijg je het mandje van vóór de klik terug.

   Anders dan de melding filteren we hier NIET op een recente klik. De balk
   moet juist ook bijwerken als iemand op de mandjepagina een aantal wijzigt
   of een regel weggooit. Dat is precies het moment waarop de klant onder een
   drempel zakt, en dan moet de balk dat zeggen.

   ---------------------------------------------------------------------------
   Waarom Shopify.routes.root en niet gewoon /cart.js
   ---------------------------------------------------------------------------
   De winkel is drietalig. In het Frans en het Engels staat er een prefix voor
   het pad, en /cart.js zonder die prefix geeft het mandje van een andere
   taalversie of een omleiding. Shopify.routes.root bevat die prefix.

   ---------------------------------------------------------------------------
   Twee instanties
   ---------------------------------------------------------------------------
   De balk staat in de drawer én op de mandjepagina. Dat zijn twee elementen
   die allebei luisteren. Elk element haalt zijn eigen /cart.js op; dat is één
   verzoek extra op een pagina waar er toch al een paar lopen, en het scheelt
   een gedeelde toestand die uit elkaar kan lopen. Zou er later een derde
   module bijkomen die ook /cart.js nodig heeft, dan trekken we het samen in
   één bestand dat het antwoord rondstuurt.

   ---------------------------------------------------------------------------
   Versiecontrole
   ---------------------------------------------------------------------------
   Shopify minificeert de CSS en haalt commentaar weg, dus een comment als
   versienummer werkt niet. Een custom property blijft wel staan:

     getComputedStyle(document.querySelector('.el-balk'))
       .getPropertyValue('--el-balk-versie')

   v1, 20 sept 2026: eerste versie.
   ========================================================================== */

(() => {
  'use strict';

  /* --- Prijsopmaak ------------------------------------------------------

     /cart.js geeft bedragen in centen en geen opgemaakte prijs. Het formaat
     van de winkel komt uit Liquid mee als data-attribuut, bijvoorbeeld
     "€{{amount_with_comma_separator}}".

     Dit is dezelfde functie als in el-mandje-melding.js. Bewust gekopieerd en
     niet gedeeld: dat bestand werkt en staat live, en ik ga het niet openen
     voor een module die er nog niet was. Komt er een derde gebruiker bij, dan
     trekken we het uit elkaar naar een eigen bestand. */

  function groepeer(tekst, duizend, decimaal) {
    const stukken = tekst.split('.');
    const heel = stukken[0].replace(/\B(?=(\d{3})+(?!\d))/g, duizend);
    return stukken[1] ? heel + decimaal + stukken[1] : heel;
  }

  function geld(centen, formaat) {
    const patroon = /\{\{\s*(\w+)\s*\}\}/;
    const gevonden = formaat.match(patroon);
    const bedrag = centen / 100;

    if (!gevonden) return bedrag.toFixed(2);

    let waarde;
    switch (gevonden[1]) {
      case 'amount':
        waarde = groepeer(bedrag.toFixed(2), ',', '.');
        break;
      case 'amount_no_decimals':
        waarde = groepeer(String(Math.round(bedrag)), ',', '');
        break;
      case 'amount_with_comma_separator':
        waarde = groepeer(bedrag.toFixed(2), '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        waarde = groepeer(String(Math.round(bedrag)), '.', '');
        break;
      case 'amount_with_apostrophe_separator':
        waarde = groepeer(bedrag.toFixed(2), "'", '.');
        break;
      default:
        waarde = bedrag.toFixed(2);
    }

    return formaat.replace(patroon, waarde);
  }

  /* --- Het element ------------------------------------------------------ */

  class MandjeBalk extends HTMLElement {
    connectedCallback() {
      this.vulling = this.querySelector('[data-balk-vulling]');
      this.tekstvak = this.querySelector('[data-balk-tekst]');
      this.punten = Array.from(this.querySelectorAll('[data-balk-punt]'));

      if (!this.vulling || !this.tekstvak) return;

      /* Staat er onzin in het attribuut, dan doet de balk niets meer in
         plaats van de pagina mee te slepen in een exception. De weergave uit
         Liquid blijft dan gewoon staan, alleen werkt hij niet meer bij. */
      try {
        this.drempels = JSON.parse(this.dataset.balkDrempels || '[]');
      } catch (e) {
        this.drempels = [];
      }

      if (!Array.isArray(this.drempels) || this.drempels.length === 0) return;

      this.formaat = this.dataset.balkGeld || '€{{amount_with_comma_separator}}';
      this.sjabloonNog = this.dataset.balkNog || '{bedrag} / {beloning}';
      this.sjabloonGehaald = this.dataset.balkGehaald || '{beloning}';
      this.tekstAlles = this.dataset.balkAlles || '';

      this.opWijziging = this.opWijziging.bind(this);
      document.addEventListener('shopify:cart:lines-update', this.opWijziging);
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:cart:lines-update', this.opWijziging);
    }

    async opWijziging(e) {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

      /* Eerst wachten tot de wijziging op de server staat. Mislukt ze, dan
         halen we het mandje toch op: dat is dan gewoon de stand van vóór de
         klik, en die klopt. */
      try {
        await (e && e.promise);
      } catch (fout) {
        /* bewust leeg */
      }

      try {
        const antwoord = await fetch(root + 'cart.js', {
          headers: { Accept: 'application/json' },
        });
        if (!antwoord.ok) return;

        const mandje = await antwoord.json();
        this.teken(mandje.total_price, mandje.item_count);
      } catch (e) {
        /* Stil falen. Een balk die even niet bijwerkt is vervelend; een
           foutmelding over een balk terwijl iemand aan het afrekenen is, is
           erger. */
      }
    }

    /* DE BEREKENING. Houd gelijk met het blok "DE BEREKENING" in
       snippets/el-mandje-balk.liquid.

       Elk segment tussen twee bolletjes is even breed in beeld, en binnen een
       segment loopt de vulling lineair. De uitleg waarom staat in het
       snippet. */
    teken(totaal, aantal) {
      /* Leeg mandje: de drawer toont dan zijn eigen lege staat en een balk
         die op nul staat is daar alleen maar ruis. */
      this.hidden = aantal === 0;
      if (this.hidden) return;

      const n = this.drempels.length;
      const segment = 100 / n;

      let gevonden = false;
      let vulling = 100;
      let ondergrens = 0;

      /* De zin voor "alle drempels gehaald". Bij één drempel is "alles
         vrijgespeeld" een rare zin — er viel maar één ding vrij te spelen —
         dus dan noemen we die beloning bij naam. Houd gelijk met het blok
         DE BEREKENING in snippets/el-mandje-balk.liquid. */
      let tekst =
        n === 1
          ? this.sjabloonGehaald.replace('{beloning}', this.drempels[0].label)
          : this.tekstAlles;

      for (let i = 0; i < n; i++) {
        const centen = this.drempels[i].centen;

        if (!gevonden) {
          if (totaal < centen) {
            gevonden = true;

            const tekort = centen - totaal;
            const spanne = centen - ondergrens;

            vulling = i * segment;
            if (spanne > 0) {
              vulling += ((totaal - ondergrens) / spanne) * segment;
            }

            tekst = this.sjabloonNog
              .replace('{bedrag}', geld(tekort, this.formaat))
              .replace('{beloning}', this.drempels[i].label);
          } else {
            ondergrens = centen;
          }
        }

        /* Het bolletje kleurt zodra de drempel gehaald is. Los van de
           vulling berekend, want een bolletje is een ja of een nee en geen
           percentage. */
        const punt = this.punten[i];
        if (punt) punt.classList.toggle('is-gehaald', totaal >= centen);
      }

      this.style.setProperty('--el-balk-vulling', Math.round(vulling * 100) / 100 + '%');

      /* Alleen schrijven als de zin echt verandert. Anders leest een
         schermlezer bij elke klik op de plusknop dezelfde zin opnieuw voor,
         ook als er niets gewijzigd is aan de boodschap. */
      if (this.tekstvak.textContent !== tekst) {
        this.tekstvak.textContent = tekst;
      }
    }
  }

  /* De balk staat twee keer op de pagina, maar dit bestand wordt door beide
     snippets ingeladen en Shopify serveert het dan één keer. Deze check is
     voor het geval iemand het later alsnog een tweede keer opneemt: define()
     met een bestaande naam gooit een fout en zet dan de hele pagina stil. */
  if (!customElements.get('el-mandje-balk')) {
    customElements.define('el-mandje-balk', MandjeBalk);
  }
})();